import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Button } from "@jdoc/ui";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/** Top-level safety net so a document-processing crash never blanks the app. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Local-only: log to console; never send document content anywhere.
    console.error("JDoc error boundary caught:", error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="max-w-md text-slate-600">
            JDoc hit an unexpected error while working with your document. Your
            file was not uploaded anywhere. You can try again.
          </p>
          <pre className="max-w-md overflow-auto rounded bg-slate-100 p-3 text-left text-xs text-slate-700">
            {this.state.error.message}
          </pre>
          <Button variant="primary" onClick={this.reset}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
